import React, { useEffect, useRef } from 'react';
import './PrivacyPolicy.css';
import NavBar3 from '../../components/NavBar3/NavBar3';
import Particles from 'react-tsparticles';
import { loadSlim } from 'tsparticles-slim';
import { motion } from 'framer-motion';

const PrivacyPolicy = () => {
  const sphereRef = useRef(null);
  const scrollPos = useRef(0);
  const spherePos = useRef(-50);

  useEffect(() => {
    const sphere = sphereRef.current;
    sphere.classList.add('animate-in');

    const handleScroll = () => {
      const currentScroll = window.scrollY;
      const scrollDirection = currentScroll > scrollPos.current ? 'down' : 'up';
      scrollPos.current = currentScroll;

      if (scrollDirection === 'down') {
        spherePos.current = Math.min(0, spherePos.current + 0.5);
      } else {
        spherePos.current = Math.max(-100, spherePos.current - 0.5);
      }

      sphere.style.transform = `translateY(${spherePos.current}%)`;
    };

    let ticking = false;
    const optimizedScroll = () => {
      if (!ticking) {
        requestAnimationFrame(() => {
          handleScroll();
          ticking = false;
        });
        ticking = true;
      }
    };

    window.addEventListener('scroll', optimizedScroll);
    return () => window.removeEventListener('scroll', optimizedScroll);
  }, []);

  const particlesInit = async (engine) => {
    await loadSlim(engine);
  };

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.2
      }
    }
  };

  const itemVariants = {
    hidden: { y: 20, opacity: 0 },
    visible: {
      y: 0,
      opacity: 1,
      transition: {
        duration: 0.5,
        ease: "easeOut"
      }
    }
  };

  return (
    <div className="privacy-policy-page">
      <div className="background-sphere" id="background-sphere" ref={sphereRef}></div>
      <Particles
        id="tsparticles"
        init={particlesInit}
        options={{
          background: { color: { value: "transparent" } },
          fpsLimit: 120,
          interactivity: {
            events: {
              onClick: { enable: true, mode: "push" },
              onHover: { enable: true, mode: "repulse" }
            },
            modes: {
              push: { quantity: 4 },
              repulse: { distance: 100, duration: 0.4 }
            }
          },
          particles: {
            color: { value: ["#FF9000", "#FFD700", "#FFFFFF"] },
            links: {
              color: "#FF9000",
              distance: 150,
              enable: true,
              opacity: 0.3,
              width: 1
            },
            move: {
              direction: "none",
              enable: true,
              outModes: { default: "bounce" },
              random: false,
              speed: 1,
              straight: false
            },
            number: { density: { enable: true }, value: 80 },
            opacity: { value: { min: 0.1, max: 0.5 } },
            shape: { type: "circle" },
            size: { value: { min: 1, max: 3 } }
          },
          detectRetina: true
        }}
      />

      <NavBar3 />

      <motion.div
        className="privacy-policy-container"
        initial="hidden"
        animate="visible"
        variants={containerVariants}
      >
        <motion.div className="privacy-policy-content" variants={itemVariants}>
          <motion.h1 variants={itemVariants}>Privacy Policy</motion.h1>
          <motion.p className="last-updated" variants={itemVariants}>
            Last Updated: {new Date().toLocaleDateString()}
          </motion.p>

          <motion.section variants={itemVariants}>
            <h2>1. Introduction</h2>
            <p>Welcome to Skript's Privacy Policy. This policy describes how we collect, use, and handle your personal information when you use our applications and services.</p>
          </motion.section>

          <motion.section variants={itemVariants}>
            <h2>2. Information We Collect</h2>
            <h3>2.1 Information you provide to us</h3>
            <ul>
              <li>Account information (name, email address, password)</li>
              <li>Profile information</li>
              <li>Communications with us</li>
            </ul>

            <h3>2.2 Information we collect automatically</h3>
            <ul>
              <li>Usage data and analytics</li>
              <li>Device information</li>
              <li>Log data</li>
              <li>Cookies and similar technologies</li>
            </ul>
          </motion.section>

          <motion.section variants={itemVariants}>
            <h2>3. How We Use Your Information</h2>
            <p>We use the information we collect to:</p>
            <ul>
              <li>Provide, maintain, and improve our services</li>
              <li>Process transactions and send related information</li>
              <li>Send administrative information</li>
              <li>Respond to comments and questions</li>
              <li>Monitor and analyze trends and usage</li>
              <li>Detect, prevent, and address technical issues</li>
            </ul>
          </motion.section>

          <motion.section variants={itemVariants}>
            <h2>4. Information Sharing and Disclosure</h2>
            <p>We may share your information with:</p>
            <ul>
              <li>Service providers who perform services on our behalf</li>
              <li>Business partners with your consent</li>
              <li>Legal requirements when necessary</li>
            </ul>
          </motion.section>

          <motion.section variants={itemVariants}>
            <h2>5. Data Security</h2>
            <p>We implement appropriate technical and organizational measures to protect your personal information against unauthorized access, alteration, disclosure, or destruction.</p>
          </motion.section>

          <motion.section variants={itemVariants}>
            <h2>6. Your Rights</h2>
            <p>You have the right to:</p>
            <ul>
              <li>Access your personal information</li>
              <li>Correct inaccurate data</li>
              <li>Request deletion of your data</li>
              <li>Object to processing of your data</li>
              <li>Data portability</li>
            </ul>
          </motion.section>

          <motion.section variants={itemVariants}>
            <h2>7. Changes to This Policy</h2>
            <p>We may update this privacy policy from time to time. We will notify you of any changes by posting the new privacy policy on this page and updating the "Last Updated" date.</p>
          </motion.section>

          <motion.section variants={itemVariants}>
            <h2>8. Contact Us</h2>
            <p>If you have any questions about this Privacy Policy, please contact us at:</p>
            <p>Email: support@skriptdev.com</p>
          </motion.section>
        </motion.div>
      </motion.div>
    </div>
  );
};

export default PrivacyPolicy; 